import { message, modal, notification } from '@repo/ui';
import { type AutomationStatus } from '@repo/utils';
import { translate, TranslationEntity } from '@repo/utils';
import { Button, type ButtonProps, Typography } from 'antd';
import { AUTOMATION_CANVAS_PATH } from 'automations/data/paths';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import { isLiveEditEnabledSelector } from 'automations/selectors/automationConfig';
import { nodesSelector } from 'automations/selectors/reactFlow';
import { useUpdateAutomationStatusMutation } from 'automations/services/automationService';
import { setCanvasAlert } from 'automations/store/automationConfigSlice';
import { didNodeSyncToServer } from 'automations/utils/node';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import styles from './UpateStatusButton.module.scss';

const MODAL_PROPS: Record<string, any> = {
  published: {
    title: translate(`Publish #{${TranslationEntity.automation}}`, [{
      capitalize: true,
    }]),
    content: translate(`Are you sure you want to publish this #{${TranslationEntity.automation}}?`),
    okText: 'Publish',
    cancelText: 'Cancel',
  },
  unpublished: {
    title: translate(`Unpublish #{${TranslationEntity.automation}}`, [{
      capitalize: true,
    }]),
    content: translate(`Are you sure you want to unpublish this #{${TranslationEntity.automation}}?`),
    okText: 'Unpublish',
    cancelText: 'Cancel',
  },
  default: {
    title: translate(`Update #{${TranslationEntity.automation}} Status`, [{
      capitalize: true,
    }]),
    content: translate(`Are you sure you want to update the status of this #{${TranslationEntity.automation}}?`),
    okText: 'Update',
    cancelText: 'Cancel',
  },
};

export interface UpateStatusButtonProps {
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  newStatus: AutomationStatus;
  children: React.ReactNode;
  afterClick?: () => void;
  successButton?: boolean;
}

export default function UpateStatusButton({
  buttonProps,
  newStatus,
  children,
  afterClick,
  successButton,
}: UpateStatusButtonProps) {
  const dispatch = useAutomationDispatch();
  const automationId = useAutomationId();
  const [updateAutomationStatus, {
    isLoading,
  }] = useUpdateAutomationStatusMutation();

  const nodes = useSelector(nodesSelector);
  const haveUnsavedChanges = useMemo(() => nodes.some((node) => !didNodeSyncToServer(node)), [nodes]);
  const isLiveEditEnabled = useSelector(isLiveEditEnabledSelector);

  const handleClick = useCallback(() => {
    if (haveUnsavedChanges || isLiveEditEnabled || nodes.length === 0) {
      let messageText = 'You have some unsaved changes. Please save the changes before updating the status.';
      if (isLiveEditEnabled) {
        messageText = ' You are in live edit mode. Please exit live edit mode before updating the status.';
      } else if (nodes.length === 0) {
        messageText = 'Please add at least one node before updating the status.';
      }
      notification.error({
        message: messageText,
        pauseOnHover: true,
        duration: 5,
        showProgress: true,
      });
      return;
    }

    updateAutomationStatus({
      id: automationId,
      status: newStatus,
    }).unwrap().then(() => {
      message.success('Status updated successfully');
      afterClick?.();
    }).catch((error) => {
      if (error?.data?.message?.includes('cycle')) {
        const involvedAutomations = error?.data?.automations;
        dispatch(setCanvasAlert({
          message: (
            <Typography.Title level={5} className={styles.errorMessage}>
              Automation Not Published
            </Typography.Title>),
          description: (
            <>
              A loop was detected in the automation. Here are the automations that are involved in the loop:
              <ol className={styles.involvedAutomations}>
                {involvedAutomations.map((automation: { name: string, id: string }) => (
                  <li key={automation.id}>
                    <a href={AUTOMATION_CANVAS_PATH(automation.id)} target="_blank" rel="noreferrer">
                      {automation.name}
                    </a>
                  </li>
                ))}
              </ol>
            </>
          ),
          type: 'error',
        }));
      }
      message.error(error?.data?.message ?? 'Failed to update status');
    });
  }, [afterClick, automationId, dispatch, haveUnsavedChanges, isLiveEditEnabled,
    newStatus, nodes.length, updateAutomationStatus,
  ]);

  const handleButtonClick = useCallback(() => {
    const modalProps = MODAL_PROPS[newStatus] ?? MODAL_PROPS.default;

    modal.confirm({
      ...modalProps,
      centered: true,
      onOk: handleClick,
    });
  }, [newStatus, handleClick]);

  return (
    <Button
      {...buttonProps}
      loading={isLoading}
      disabled={isLoading}
      onClick={handleButtonClick}
      className={clsx(
        buttonProps?.className,
        successButton && styles.successButton,
      )}
    >
      {children}
    </Button>
  );
}
